import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { reduxForm } from 'redux-form';
import Validation from 'infrastructure/js/components/controls/controlsValidations.js';
import InputSection from 'infrastructure/js/components/Dialog/InputSection/inputSection';
import TextField from 'infrastructure/js/components/controls/TextField/textField';
import Header, { headerItemsLabels } from '../../../../Common/Header/header';
import Switch from 'infrastructure/js/components/controls/Switch/switch';
import useLabels from 'infrastructure/js/hooks/useLabels';
import TimeField from 'infrastructure/js/components/controls/TimeField/timeField';
import DateTimeHelper from 'infrastructure/js/utils/dateTimeHelper';

require('./sectionSettingsForm.scss');

const maxLength50 = Validation.maxLength(50);

const validateMaximumTimeMin = (value, allValues) => {
  const minValue = {
    _hours: 0,
    _minutes: 1
  }
  const currentValue = {
    _hours: allValues.maximumTime_hours,
    _minutes: allValues.maximumTime_minutes
  }
  return Validation.timeField.min(minValue)(currentValue, allValues)
};

const validateMaxixmumTimeRequired = (value, allValues) => {
  return Validation.timeRequired('maximumTime')(value, allValues);
};

const SectionSettingsForm = ({ initialize = () => { console.error('initialize is missing!'); },
  pristine = true,
  handleSubmit = () => { console.error('handleSubmit is missing!'); },
  actions,
  change = () => { console.error('change is missing!'); },
  itemToEdit = null,
  onSave = () => { },
  onDelete = () => { }
}) => {

  const labels = useLabels('mat.administration.matsettings.digitalTwin.section.form.');

  const [isDisallowParallelSetups, setIsDisallowParallelSetups] = useState(itemToEdit?.isDisallowParallelSetup || false);


  useEffect(() => {
    initFormValues();
  }, [itemToEdit]);

  const initFormValues = () => {
    let initialValues =
    {
      disallowParallelSetups: false,
      maximumTime_minutes: '',
      maximumTime_hours: '',

      humanCapacitySharingEnabled: false,
    };

    if (isEditMode()) {

      initialValues.sectionName = itemToEdit?.name;

      initialValues.disallowParallelSetups = itemToEdit?.isDisallowParallelSetup;
      initialValues.maximumTime_minutes = itemToEdit?.isDisallowParallelSetup ? itemToEdit.maxParallelSetupTime % 60 | 0 : '';
      initialValues.maximumTime_hours = itemToEdit?.isDisallowParallelSetup ? itemToEdit.maxParallelSetupTime / 60 | 0 : '';

      initialValues.humanCapacitySharingEnabled = itemToEdit?.isHumanCapacitySharingEnabled;
    }

    setIsDisallowParallelSetups(itemToEdit?.isDisallowParallelSetup || false);

    initialize(initialValues);
  }

  const isEditMode = () => {
    return itemToEdit?.id;
  }

  //the root section is not editable
  const isReadOnly = () => {
    return itemToEdit && itemToEdit.id && itemToEdit.parentId === null;
  }

  const onSubmit = (data) => {
    let newData = {
      name: data.sectionName,

      disallowParallelSetup: data.disallowParallelSetups,
      maxParallelSetupTime: DateTimeHelper.ConvertHoursMinutesToMinutes(data.maximumTime_hours, data.maximumTime_minutes),

      humanCapacitySharingEnabled: data.humanCapacitySharingEnabled,

      parentId: itemToEdit.parentId,
      prevSiblingId: null,
      nextSiblingId: null,
    };

    if (isEditMode()) {
      newData.id = itemToEdit.id;
      newData.parentId = null;  // do not send parentId on Edit (Server side request)
    }

    actions?.viewActions?.submit(newData, isEditMode(), false, true)
      .then((response) => {
        if (response && response.success) {
          onSave?.(response.data);
        }
      });
  };

  const onDeleteHandler = () => {
    actions?.viewActions?.delete(itemToEdit.id).then((response) => {
      if (response && response.success) {
        onDelete?.();
      }
    });
  }

  const onDisallowParallelSetupsHandler = (value) => {
    change('maximumTime_hours', '');
    change('maximumTime_minutes', '');
    setIsDisallowParallelSetups(value);
  }

  const getHeaderItems = () => {
    return {
      title: isReadOnly() ? ' ' : isEditMode() ? labels.get('title.edit') : labels.get('title.create'),
      buttons: [
        {
          id: 'save',
          label: headerItemsLabels.SAVE,
          className: 'no-icon',
          disabled: pristine || isReadOnly(),
          action: handleSubmit(onSubmit),
        },
        ...(isEditMode() ? [
          {
            id: 'delete',
            label: headerItemsLabels.DELETE,
            className: 'no-icon',
            disabled: isReadOnly(),
            action: onDeleteHandler
          }] : []),
      ]
    }
  };

  let headerItems = getHeaderItems();

  return (
    <div className='pl-form section-settings-form'>
      <Header {...headerItems} />

      <div className='section'>
        <InputSection label={labels.get('name') + '*'} htmlFor="sectionName">
          <TextField id="sectionName" name="sectionName"
            maxLength={50}
            validate={[Validation.required, maxLength50]}
            disabled={isEditMode() && itemToEdit && !itemToEdit.parentId}
          />
        </InputSection>
        <InputSection />
      </div>

      <div className='section'>
        <div className='switch-section'>
          <label>{labels.get('disallowParallelSetups')}</label>
          <Switch id="disallowParallelSetups" name="disallowParallelSetups"
            onChangeCallback={onDisallowParallelSetupsHandler}
          />
        </div>
        <InputSection label={labels.get('maximumTime')} htmlFor="maximumTime"
          className='maximum-time-section'>
          <TimeField id="maximumTime" name="maximumTime" className='maximum-time'
            disabled={!isDisallowParallelSetups}
            validate={isDisallowParallelSetups ? [validateMaxixmumTimeRequired, validateMaximumTimeMin] : undefined}
            hoursCount={true} maxHoursLength={2} />
        </InputSection>
      </div>

      <div className='section'>
        <div className='switch-section'>
          <label>{labels.get('humanCapacitySharingEnabled')}</label>
          <Switch id="humanCapacitySharingEnabled" name="humanCapacitySharingEnabled" />
        </div>
      </div>

    </div>
  );
}

SectionSettingsForm.propTypes = {
  itemToEdit: PropTypes.object,
  actions: PropTypes.object.isRequired,
  change: PropTypes.func,
  handleSubmit: PropTypes.func,
  initialize: PropTypes.func,
  pristine: PropTypes.bool,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
};

export default reduxForm({
  form: 'sectionSettingsForm',
}
)(SectionSettingsForm);




